<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useSafeLocalStorage } from 'composables'

    /**
     * Not in lib.dom: Chromium fires this instead of showing its own install
     * UI once `preventDefault()` is called on it.
     */
    interface BeforeInstallPromptEvent extends Event {
        prompt: () => Promise<void>
        userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
    }

    defineProps<{
        /**
         * A new service worker is waiting: the app shell owns this state,
         * because `virtual:pwa-register` only exists in the frontend build.
         */
        updateAvailable?: boolean
        /** The update is being applied and the page is about to reload. */
        updating?: boolean
    }>()

    const emit = defineEmits<{
        update: []
        dismissUpdate: []
    }>()

    const { t } = useI18n()
    const { t: $t } = useI18n({ useScope: 'global' })

    // #region install
    const DISMISSED_KEY = 'pwa-install-dismissed'
    const dismissed = useSafeLocalStorage<boolean>(DISMISSED_KEY, false)

    const deferredPrompt = ref<BeforeInstallPromptEvent>()
    const isInstalling = ref(false)

    /** Already installed: standalone display mode, or iOS legacy flag. */
    const isStandalone = () =>
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as Navigator & { standalone?: boolean })
            .standalone === true

    /**
     * iOS has no `beforeinstallprompt`: installing is a manual gesture from
     * the share sheet, so all we can do is explain it.
     */
    const isIos = () => {
        const ua = window.navigator.userAgent
        return (
            (/iphone|ipad|ipod/i.test(ua) ||
                // iPadOS 13+ reports a Macintosh UA; touch support tells
                // it apart from actual Macs.
                (/macintosh/i.test(ua) &&
                    window.navigator.maxTouchPoints > 1)) &&
            // Exclude in-app browsers, where "Add to Home Screen" is
            // unavailable.
            !/crios|fxios|edgios/i.test(ua)
        )
    }

    const showIosHint = ref(false)
    /**
     * iOS has no engagement heuristic of its own, so the hint would otherwise
     * appear on the very first paint, on top of the loading spinner.
     */
    const IOS_HINT_DELAY_MS = 5000
    let iosHintTimeout: ReturnType<typeof setTimeout> | undefined

    const showInstall = computed(
        () => !dismissed.value && (!!deferredPrompt.value || showIosHint.value),
    )

    const onBeforeInstallPrompt = (event: Event) => {
        // Suppress the browser's own mini-infobar and drive the prompt from
        // our own UI instead.
        event.preventDefault()
        deferredPrompt.value = event as BeforeInstallPromptEvent
    }

    const onInstalled = () => {
        deferredPrompt.value = undefined
        showIosHint.value = false
        dismissed.value = true
    }

    onMounted(() => {
        if (isStandalone()) {
            return
        }
        window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
        window.addEventListener('appinstalled', onInstalled)
        if (isIos()) {
            iosHintTimeout = setTimeout(() => {
                showIosHint.value = true
            }, IOS_HINT_DELAY_MS)
        }
    })

    onBeforeUnmount(() => {
        window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
        window.removeEventListener('appinstalled', onInstalled)
        clearTimeout(iosHintTimeout)
    })

    const onInstall = async () => {
        const event = deferredPrompt.value
        if (!event) {
            return
        }
        isInstalling.value = true
        try {
            await event.prompt()
            const { outcome } = await event.userChoice
            if (outcome === 'dismissed') {
                // The browser will not re-fire the event for this page load.
                dismissed.value = true
            }
        } finally {
            isInstalling.value = false
            deferredPrompt.value = undefined
        }
    }

    const onDismissInstall = () => {
        dismissed.value = true
    }
    // #endregion
</script>

<template>
    <!--
        A single group for both notices, so they queue up in one column instead
        of fighting for the corners of the viewport. Each alert must be a direct
        child with its own key: the group wraps them in a `<TransitionGroup>`
        that only animates keyed vnodes it owns.
        The offline state is not here: it covers the whole app (PkOfflineScreen).
    -->
    <VvAlertGroup
        class="safe-area-block-end"
        name="pwa"
        inline="end"
        block="bottom"
        position="fixed">
        <VvAlert
            v-if="showInstall"
            key="install"
            role="alertdialog"
            modifiers="notification">
            {{ t('installText') }}
            <small class="block text-smaller mt-4">
                {{ showIosHint ? t('installHintIos') : t('installHint') }}
            </small>
            <template #footer>
                <VvButtonGroup class="ml-auto">
                    <VvButton
                        v-if="!showIosHint"
                        modifiers="action"
                        :label="t('installAction')"
                        :loading="isInstalling"
                        :disabled="isInstalling"
                        @click="onInstall" />
                    <VvButton
                        modifiers="action-quiet"
                        :label="$t('action.cancel')"
                        :disabled="isInstalling"
                        @click="onDismissInstall" />
                </VvButtonGroup>
            </template>
        </VvAlert>

        <VvAlert
            v-if="updateAvailable"
            key="update"
            role="alertdialog"
            modifiers="notification">
            {{ t('updateText') }}
            <small class="block text-smaller mt-4">
                {{ t('updateInfo') }}
            </small>
            <template #footer>
                <VvButtonGroup class="ml-auto">
                    <VvButton
                        modifiers="action"
                        :label="$t('action.update')"
                        :loading="updating"
                        :disabled="updating"
                        @click="emit('update')" />
                    <VvButton
                        modifiers="action-quiet"
                        :label="$t('action.cancel')"
                        :disabled="updating"
                        @click="emit('dismissUpdate')" />
                </VvButtonGroup>
            </template>
        </VvAlert>
    </VvAlertGroup>
</template>

<i18n lang="json">
{
    "en": {
        "installText": "Install Agent Studio on this device.",
        "installHint": "It opens in its own window, without the browser bar.",
        "installHintIos": "Tap the Share button in Safari, then \"Add to Home Screen\".",
        "installAction": "Install",
        "updateText": "A new update is available. Do you want to proceed with the installation?",
        "updateInfo": "The browser will be reloaded automatically."
    },
    "it": {
        "installText": "Installa Agent Studio su questo dispositivo.",
        "installHint": "Si apre in una finestra propria, senza la barra del browser.",
        "installHintIos": "Tocca il pulsante Condividi in Safari, poi \"Aggiungi a Home\".",
        "installAction": "Installa",
        "updateText": "È disponibile un nuovo aggiornamento. Vuoi procedere con l'installazione?",
        "updateInfo": "Il browser verrà ricaricato automaticamente."
    }
}
</i18n>
